import { useState } from 'react'

import { TextInput, Tooltip } from '@mantine/core'
import { useDebouncedValue, useDisclosure } from '@mantine/hooks'

import { useMutation, useQuery } from '@redwoodjs/web'

import AdminContainer from 'src/components/AdminContainer/AdminContainer'
import DNButton from 'src/components/Buttons/Button/DNButton'
import DeleteModal from 'src/components/Overlays/DeleteModal/DeleteModal'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  DELETE_DOCUMENT_TEMPLATE,
  GET_DOCUMENT_TEMPLATES,
} from 'src/graphql/documentTemplates.graphql'
import usePage from 'src/hooks/usePage/usePage'
import usePageSize from 'src/hooks/usePageSize/usePageSize'
import IconSearch from 'src/icons/IconSearch'

import DocumentTemplateDrawer from './components/DocumentTemplateDrawer'
import DocumentTemplatesTable from './components/DocumentTemplatesTable'

interface DocumentTemplate {
  documentTemplateId: string
  documentName: string
  signingToolRefId: string
  documentCategory: string
  signers: string[]
  userStatus: string
  status: string
}

const DocumentTemplatesTab = () => {
  const [page, setPage] = usePage()
  const [pageSize, setPageSize] = usePageSize(20)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebouncedValue(searchTerm, 300)

  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  )
  const [templateToDelete, setTemplateToDelete] =
    useState<DocumentTemplate | null>(null)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('edit')

  const [drawerOpened, { open: openDrawer, close: closeDrawer }] =
    useDisclosure(false)
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false)

  const { data, loading } = useQuery(GET_DOCUMENT_TEMPLATES, {
    variables: { searchTerm: debouncedSearchTerm || undefined },
  })

  const [deleteTemplate] = useMutation(DELETE_DOCUMENT_TEMPLATE, {
    refetchQueries: [GET_DOCUMENT_TEMPLATES],
    onCompleted: () => {
      toast('Template deleted successfully', 'success')
    },
    onError: () => {
      toast('Unable to delete template, please try again.', 'error')
    },
  })

  const handleAddTemplate = () => {
    setSelectedTemplateId(null)
    setDrawerMode('create')
    openDrawer()
  }

  const handleRowClick = (templateId: string) => {
    setSelectedTemplateId(templateId)
    setDrawerMode('edit')
    openDrawer()
  }

  const handleDeleteClick = (template: DocumentTemplate) => {
    setTemplateToDelete(template)
    openDeleteModal()
  }

  const handleConfirmDelete = async () => {
    if (templateToDelete) {
      await deleteTemplate({
        variables: {
          documentTemplateId: templateToDelete.documentTemplateId,
        },
      })
      setTemplateToDelete(null)
    }
  }

  const handleCloseDrawer = () => {
    setSelectedTemplateId(null)
    closeDrawer()
  }

  const templates = data?.documentTemplates ?? []

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="flex flex-shrink-0 flex-row justify-between">
        <div className="flex flex-col">
          <div className="text-lg font-semibold text-doubleNickel-gray-900">
            Document Templates
          </div>
          <div className="text-sm text-doubleNickel-gray-600">
            {templates.length} template{templates.length !== 1 ? 's' : ''}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <TextInput
            placeholder="Search templates..."
            leftSection={
              <IconSearch className="h-4 w-4 fill-none stroke-doubleNickel-gray-500" />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.currentTarget.value)}
            className="w-64"
          />
          <AdminContainer>
            <div className="flex items-center gap-2">
              <Tooltip label="Admin only">
                <div className="h-2 w-2 rounded-full bg-doubleNickel-error-400" />
              </Tooltip>
              <DNButton
                variant="primary"
                onClick={handleAddTemplate}
              >
                Add Template
              </DNButton>
            </div>
          </AdminContainer>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <DocumentTemplatesTable
          templates={templates}
          loading={loading}
          onRowClick={handleRowClick}
          onDeleteClick={handleDeleteClick}
          totalCount={templates.length}
          defaultResultsPerPage={pageSize}
          onPageChange={setPage}
          paginationProps={{
            initialPage: page,
            onResultsPerPageChange: setPageSize,
          }}
        />
      </div>

      <DocumentTemplateDrawer
        opened={drawerOpened}
        onClose={handleCloseDrawer}
        templateId={selectedTemplateId}
        mode={drawerMode}
      />

      <DeleteModal
        title="Delete Template"
        description={
          templateToDelete ? (
            <div className="space-y-2">
              <p>Are you sure you want to delete this template?</p>
              <div className="rounded-md bg-doubleNickel-gray-50 p-3">
                <p className="font-medium text-doubleNickel-gray-700">
                  {templateToDelete.documentName}
                </p>
              </div>
            </div>
          ) : (
            'This action cannot be undone.'
          )
        }
        opened={deleteModalOpened}
        close={closeDeleteModal}
        onDelete={handleConfirmDelete}
      />
    </div>
  )
}

export default DocumentTemplatesTab
